import { useEffect, useState } from "react";
import { MapPin, Plus, Trash2, LocateFixed, Pencil } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { WorkCenterMapPicker } from "@/components/WorkCenterMapPicker";
import { WORK_CENTERS } from "@/constants/testIds";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", latitude: "", longitude: "", radius_meters: "150" };

export default function WorkCenters() {
  const [centers, setCenters] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/work-centers").then(({ data }) => setCenters(data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (c) => { setEditingId(c.id); setForm({ name: c.name, latitude: String(c.latitude), longitude: String(c.longitude), radius_meters: String(c.radius_meters) }); setOpen(true); };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Tu navegador no soporta geolocalización");
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, latitude: String(pos.coords.latitude), longitude: String(pos.coords.longitude) })),
      () => toast.error("No se pudo obtener tu ubicación. Revisa el permiso de ubicación del navegador."),
    );
  };

  const save = async () => {
    setSaving(true);
    const payload = { name: form.name, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), radius_meters: parseInt(form.radius_meters, 10) };
    try {
      if (editingId) await api.patch(`/work-centers/${editingId}`, payload);
      else await api.post("/work-centers", payload);
      toast.success(editingId ? "Centro de trabajo actualizado" : "Centro de trabajo creado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/work-centers/${id}`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight flex items-center gap-3">
            <MapPin className="w-7 h-7 text-petroleum" strokeWidth={1.5} /> Centros de Trabajo
          </h1>
          <p className="text-charcoal/60 mt-1">Ubicación y radio de tolerancia para comparar el marcaje de autoservicio y asignar kioskos.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={WORK_CENTERS.newButton} onClick={openCreate} className="bg-petroleum rounded-full"><Plus className="w-4 h-4 mr-1" /> Nuevo centro</Button>
          </DialogTrigger>
          <DialogContent data-testid={WORK_CENTERS.dialog} className="bg-bone max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Editar centro de trabajo" : "Nuevo centro de trabajo"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input data-testid={WORK_CENTERS.nameInput} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej. Sucursal Centro" />
              </div>
              <Button data-testid={WORK_CENTERS.useMyLocationButton} type="button" variant="outline" size="sm" onClick={useMyLocation}>
                <LocateFixed className="w-4 h-4 mr-1.5" /> Usar mi ubicación actual
              </Button>
              <WorkCenterMapPicker
                latitude={form.latitude}
                longitude={form.longitude}
                radiusMeters={form.radius_meters}
                onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Latitud</Label>
                  <Input data-testid={WORK_CENTERS.latitudeInput} type="number" step="any" value={form.latitude} onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))} />
                </div>
                <div>
                  <Label>Longitud</Label>
                  <Input data-testid={WORK_CENTERS.longitudeInput} type="number" step="any" value={form.longitude} onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Radio de tolerancia (metros)</Label>
                <Input data-testid={WORK_CENTERS.radiusInput} type="number" min="10" max="5000" value={form.radius_meters} onChange={(e) => setForm((f) => ({ ...f, radius_meters: e.target.value }))} />
              </div>
              <Button data-testid={WORK_CENTERS.saveButton} disabled={saving || !form.name || !form.latitude || !form.longitude} onClick={save} className="w-full bg-petroleum rounded-full">
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 bg-card border border-petroleum/10 rounded-xl divide-y divide-petroleum/10">
        {centers.map((c) => (
          <div key={c.id} data-testid={WORK_CENTERS.row} className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-semibold text-charcoal">{c.name}</p>
              <p className="text-xs text-charcoal/50">{c.latitude != null ? c.latitude.toFixed(5) : "—"}, {c.longitude != null ? c.longitude.toFixed(5) : "—"} · Radio {c.radius_meters ?? "—"} m</p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid={`${WORK_CENTERS.editButton}-${c.id}`} size="sm" variant="outline" onClick={() => openEdit(c)}><Pencil className="w-4 h-4" /></Button>
              <Button data-testid={`${WORK_CENTERS.deleteButton}-${c.id}`} size="sm" variant="outline" onClick={() => remove(c.id)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {centers.length === 0 && <p className="p-6 text-sm text-charcoal/50">Sin centros de trabajo registrados. Crea uno para poder comparar la geolocalización de los marcajes de autoservicio.</p>}
      </div>
    </AdminLayout>
  );
}
